import { useEffect, useState } from "react";

import { TableAndFiltersContainer } from "features/layout/components";

import { ColumnsVisibilityControl } from "common/components/DataTable";
import useColumnVisibility from "common/components/DataTable/useColumnVisibility";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import PaginationFooter from "common/components/PaginationFooter";
import RenderIf from "common/components/RenderIf";
import usePagination from "common/hooks/usePagination";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { SelectedFacetParam } from "common/types";

import { LIST_LEADS_COLUMNS } from "../../../../constants";
import useIncludedListLeads from "../../../../datahooks/useIncludedListLeads";
import useListLeadsParams from "../../../../hooks/useListLeadsParams";
import DesktopFilters from "../Filters/DesktopFilters";
import MobileFilters from "../Filters/MobileFilters";
import ManageListLeads from "../ManageListLeads";
import NoLeadsPlaceholder from "../NoLeadsPlaceholder";
import NoResultsPlaceholder from "../NoResultsPlaceholder";
import CreateVariableDialog from "./CreateVariableDialog";
import DesktopTable from "./DesktopTable";
import MobileList from "./MobileList";

import type { JSX } from "react";
import type { ListLead, ListLeadsSearchParams } from "../../../../types";

interface IncludedLeadsProps {
  listId: string;
  searchParams: ListLeadsSearchParams<SelectedFacetParam>;
  setSearchParams: (
    searchParams: ListLeadsSearchParams<SelectedFacetParam>,
  ) => void;
  //the search input LeadListLeads owns, the same for both tabs
  searchComponent: JSX.Element;
}

export default function IncludedLeads({
  listId,
  searchParams,
  setSearchParams,
  searchComponent,
}: IncludedLeadsProps) {
  const isDesktop = useTwBreakpoint("lg");

  //the leads-search returns the list's own leads, so does the selection
  const [selectedLeads, setSelectedLeads] = useState<ListLead[]>([]);
  const [isCreateVariableOpen, setIsCreateVariableOpen] = useState(false);

  const { page, setPage, pageSize, setPageSize } =
    usePagination("lead-list-leads");
  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    "lead-list-leads",
    LIST_LEADS_COLUMNS,
  );
  const { hasFilters, resetParams } = useListLeadsParams(
    searchParams,
    setSearchParams,
  );

  const {
    listLeads,
    listLeadsTotal,
    listLeadsFacets,
    customVariableKeys,
    isFetchingListLeads,
    listLeadsError,
    refetchListLeads,
  } = useIncludedListLeads({
    listId,
    searchParams,
    pagingParams: { page, pageSize },
  });

  //any change to the search invalidates the current page, so does opening the tab
  useEffect(() => {
    setPage(0);
  }, [searchParams]);

  //the selection only covers the rows of the page that's listed
  useEffect(() => {
    setSelectedLeads([]);
  }, [searchParams, page]);

  if (listLeadsError) {
    return (
      <ErrorPlaceholder
        errorMessage="Error loading leads"
        onRetry={refetchListLeads}
      />
    );
  }

  const hasSearch = !!searchParams.keyword || hasFilters;
  const hasNoLeads = !isFetchingListLeads && !listLeads?.length;

  //there's nothing to search or filter through, so only the placeholder is shown
  if (hasNoLeads && !hasSearch) {
    return <NoLeadsPlaceholder type="included" />;
  }

  const totalItemCount = listLeadsTotal || 0;

  const filtersProps = {
    searchParams,
    setSearchParams,
    facets: listLeadsFacets,
    isLoadingFacets: isFetchingListLeads,
  };

  const tableProps = {
    leads: listLeads,
    isLoading: isFetchingListLeads,
    pageSize,
    totalItemCount,
    selectedLeads,
    setSelectedLeads,
    listId,
  };

  const createVariableDialog = (
    <CreateVariableDialog
      listId={listId}
      isOpen={isCreateVariableOpen}
      onClose={() => setIsCreateVariableOpen(false)}
    />
  );

  const content = hasNoLeads ? (
    <NoResultsPlaceholder onClear={resetParams} />
  ) : (
    <>
      {isDesktop ? (
        <DesktopTable
          {...tableProps}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
          customVariableKeys={customVariableKeys}
        />
      ) : (
        <MobileList {...tableProps} />
      )}

      <PaginationFooter
        pageSize={pageSize}
        setPageSize={setPageSize}
        selectedPage={page}
        setSelectedPage={setPage}
        totalItemCount={totalItemCount}
        isLastPageHidden={false}
      />
    </>
  );

  if (isDesktop) {
    return (
      <>
        <TableAndFiltersContainer
          searchComponent={searchComponent}
          filtersComponent={<DesktopFilters {...filtersProps} />}
          columnSelectionComponent={
            <ColumnsVisibilityControl
              columns={LIST_LEADS_COLUMNS}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              onAddColumn={() => setIsCreateVariableOpen(true)}
            />
          }
        >
          {content}
        </TableAndFiltersContainer>

        {createVariableDialog}
      </>
    );
  }

  return (
    <>
      <div className="flex gap-x-2.5">
        {searchComponent}
        <MobileFilters {...filtersProps} />
      </div>

      <RenderIf condition={!!selectedLeads.length}>
        <ManageListLeads
          listId={listId}
          isExcluded={false}
          selectedLeads={selectedLeads}
        />
      </RenderIf>

      {content}

      {createVariableDialog}
    </>
  );
}
